import React, { useMemo } from 'react';
import { Popover, Box } from '@mui/material';
import { useTranslation } from 'react-i18next';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import BadgeAvatar from '@src/components/BadgeAvatar';
import Typography from '@src/new-components/Typography';
import CommonButton from '@src/new-components/Button';
import { COLOR } from '@src/styles/color';
import { DEFAULT_SPEED } from '@src/constants/voice';
import { MAX_SPEAKER_FREE_LIMIT, MAX_SPEAKERS } from '@src/constants/project';
import { FREE_PACKAGE_CODES } from '@src/constants/package';
import { useDispatch } from 'react-redux';
import actions from '@src/redux/actions';
import CrownImg from '@src/assets/images/crown.png';
import { useHistory } from 'react-router-dom';
import route from '@src/constants/route';
import LimitedFeature from '@src/components/LimitedFeature';
import { SUB_STATUS, VOICE_STATUS } from '@src/constants/voiceCloning';
import moment from 'moment';
import { convertFormatDate } from '@src/utils/date';
import Tooltip from '@src/components/Tooltip';
import RunningWithErrorsRoundedIcon from '@mui/icons-material/RunningWithErrorsRounded';
import WarningRoundedIcon from '@mui/icons-material/WarningRounded';
import {
  StyledPopoverContent,
  StyledSpeakersList,
  StyledAddSpeakerButton,
} from './index.style';

const SpeakerSelectionPopover = ({
  open,
  anchorEl,
  onClose,
  speakers = [],
  voiceDefault,
  currentSpeaker,
  onAddSpeaker,
  onSelectSpeaker,
  user,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const history = useHistory();

  const defaultSpeaker = useMemo(
    () => ({
      name: t('defaultSpeakerName'),
      voice: voiceDefault,
      speed: DEFAULT_SPEED,
    }),
    [],
  );

  const usingFreePackage = FREE_PACKAGE_CODES.includes(user?.packageCode);

  const maxSpeakersLimit = usingFreePackage
    ? MAX_SPEAKER_FREE_LIMIT
    : MAX_SPEAKERS;

  const speakersList = useMemo(
    () => (!speakers || speakers.length === 0 ? [defaultSpeaker] : speakers),
    [speakers, defaultSpeaker],
  );

  const onOpenAddSpeaker = () => {
    if (speakersList.length >= maxSpeakersLimit && usingFreePackage) {
      dispatch(
        actions.limitedFeaturesDialog.showLimitedFeaturesDialog({
          description: t('limitedFeatureAddSpeaker'),
          img: CrownImg,
          classNameImg: 'custom-img',
          maxWidth: '512px',
          onUpgrade: () => {
            history.push(route.PAYMENT);
            dispatch(actions.limitedFeaturesDialog.hideLimitedFeaturesDialog());
          },
        }),
      );
      return;
    }
    onAddSpeaker();
  };
  const renderVoiceItem = (speaker) => {
    const displayName = speaker?.name || t('defaultSpeakerName');
    const displayLocation = speaker?.voice?.name || '';
    const isSelected = currentSpeaker?.id === speaker?.id;
    const voiceInActive = !speaker?.voice?.active;
    const isInValidPublicVoiceCloning =
      (speaker?.voice?.userId !== user?.id &&
        ((speaker?.voice?.discardAt &&
          moment().isAfter(speaker?.voice?.discardAt)) ||
          speaker?.voice?.status === VOICE_STATUS.PRIVATE)) ||
      voiceInActive;

    const isPublicVoiceCloning = speaker?.voice?.status === SUB_STATUS.PUBLIC;
    const isVoiceCloningAboutToRemoved =
      isPublicVoiceCloning && speaker?.voice?.discardAt
        ? moment().isBefore(speaker?.voice?.discardAt)
        : false;
    return (
      <CommonButton
        key={speaker?.id}
        variant="transparent"
        onClick={() => {
          if (onSelectSpeaker) {
            onSelectSpeaker(speaker);
          }
          onClose();
        }}
        customStyle={{
          height: '56px',
          minHeight: '56px',
          maxHeight: '56px',
          borderRadius: '8px',
          padding: '8px 12px',
          width: '100%',
          justifyContent: 'flex-start',
          gap: '8px',
          backgroundColor: isSelected ? COLOR.secondary[80] : 'transparent',
          boxSizing: 'border-box',
        }}
      >
        <div style={{ marginRight: '8px' }}>
          <BadgeAvatar
            width={24}
            smallImgWidth={12}
            img={speaker?.voice?.roundImage}
            smallImg={speaker?.voice?.language?.roundImage}
            type="image"
          />
        </div>
        <Box
          display="flex"
          flexDirection="column"
          gap="2px"
          alignItems="flex-start"
        >
          <Box display="flex" gap="4px" alignItems="center">
            <Typography variant="body-14-medium" color={COLOR.darkGray}>
              {displayName}
            </Typography>
            <LimitedFeature voiceFeatures={speaker?.voice?.features} />
            {isVoiceCloningAboutToRemoved && (
              <Tooltip
                arrow
                title={t('voiceCloningDiscardAt', {
                  date: convertFormatDate(speaker?.voice?.discardAt),
                })}
                placement="top"
              >
                <RunningWithErrorsRoundedIcon
                  sx={{
                    color: COLOR.redPrimary,
                    fontSize: '14px',
                    cursor: 'pointer',
                  }}
                />
              </Tooltip>
            )}
            {isInValidPublicVoiceCloning && (
              <Tooltip
                arrow
                title={t('invalidPublicVoiceCloning')}
                placement="top"
              >
                <WarningRoundedIcon
                  sx={{
                    color: COLOR.redPrimary,
                    fontSize: '14px',
                    cursor: 'pointer',
                  }}
                />
              </Tooltip>
            )}
          </Box>

          {displayLocation && (
            <Typography
              variant="body-12-regular"
              color={COLOR.label2[64]}
              style={{ lineHeight: '16px' }}
            >
              {displayLocation}
            </Typography>
          )}
        </Box>
      </CommonButton>
    );
  };

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'left',
      }}
      PaperProps={{
        style: {
          zIndex: 999999,
          borderRadius: '8px',
          marginTop: '8px',
        },
        sx: {
          boxShadow: '0px 4px 16px 0px rgba(0,0,0,0.08) !important',
        },
      }}
    >
      <StyledPopoverContent>
        <StyledSpeakersList className="scrollbar-custom">
          {speakersList.map((speaker) => renderVoiceItem(speaker))}
        </StyledSpeakersList>
        <StyledAddSpeakerButton>
          <CommonButton
            startIcon={
              <AddRoundedIcon
                sx={{
                  width: 24,
                  height: 24,
                  color: COLOR.darkBlue,
                }}
              />
            }
            disabled={
              speakersList.length >= maxSpeakersLimit && !usingFreePackage
            }
            variant="outlined"
            onClick={onOpenAddSpeaker}
            customStyle={{
              height: '40px',
              borderRadius: '8px',
              padding: '8px 8px',
              width: '157px',
            }}
            textProps={{
              color: COLOR.darkBlue,
              variantText: 'body-14-medium',
            }}
          >
            {t('addSpeaker')}
          </CommonButton>
          <Typography variant="body-14-medium" color={COLOR.darkGray}>
            {speakersList.length}/{maxSpeakersLimit}
          </Typography>
        </StyledAddSpeakerButton>
      </StyledPopoverContent>
    </Popover>
  );
};

export default SpeakerSelectionPopover;
